import { memo, useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@/components/atoms/Icon';

const STORAGE_KEY = 'aiEditsTooltipDismissed';

export const hasDismissedAIEditsTooltip = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const markAIEditsTooltipAsDismissed = (): void => {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Ignore localStorage errors
  }
};

interface AIEditsTooltipProps {
  buttonRef: React.RefObject<HTMLButtonElement>;
  onDismiss?: () => void;
}

// Fixed arrow position relative to tooltip (higher up, like original design)
const ARROW_TOP_OFFSET = 14.93; // Fixed position from top of tooltip
const ARROW_HEIGHT = 14; // Total arrow height (7px border triangle)
const ARROW_CENTER_OFFSET = ARROW_TOP_OFFSET + ARROW_HEIGHT / 2; // Center of arrow relative to tooltip top

const AIEditsTooltip = memo(({ buttonRef, onDismiss }: AIEditsTooltipProps) => {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    markAIEditsTooltipAsDismissed();
    onDismiss?.();
  }, [onDismiss]);

  useEffect(() => {
    if (!buttonRef.current) {
      return;
    }

    const updatePosition = () => {
      const buttonRect = buttonRef.current?.getBoundingClientRect();
      if (!buttonRect) {
        return;
      }

      // Calculate button center Y position
      const buttonCenterY = buttonRect.top + buttonRect.height / 2;

      // Position tooltip so that the arrow (at fixed position ARROW_CENTER_OFFSET) aligns with button center
      // Tooltip top = buttonCenterY - ARROW_CENTER_OFFSET
      const tooltipTop = buttonCenterY - ARROW_CENTER_OFFSET;

      // Position tooltip to the left of the button (since menu is on the right side)
      const tooltipWidth = 280;
      const tooltipLeft = buttonRect.left - tooltipWidth - 12; // 12px gap from button

      setPosition({
        top: tooltipTop,
        left: tooltipLeft
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [buttonRef]);

  if (!position) {
    return null;
  }

  return (
    <>
      <div
        ref={tooltipRef}
        className="ai-edits-tooltip fixed z-[60] rounded-[10px] border border-slate-200 bg-white shadow-lg"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: 'max-content',
          maxWidth: '280px'
        }}
      >
        {/* Arrow pointing to the right (towards the button) - fixed position higher on tooltip */}
        <div
          className="absolute right-[-5px] flex items-center justify-center"
          style={{
            top: `${ARROW_TOP_OFFSET}px`
          }}
        >
          {/* Triangle arrow with border - using two triangles to create border effect */}
          <div className="relative">
            {/* Border triangle (slightly larger, behind) */}
            <div className="absolute size-0 border-y-[7px] border-l-[7px] border-y-transparent border-l-slate-200" />
            {/* White triangle (on top) */}
            <div className="relative size-0 border-y-[6px] border-l-[6px] border-y-transparent border-l-white" />
          </div>
        </div>

        {/* Content */}
        <div className="relative flex flex-col gap-2 p-3">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute right-[10px] top-[10px] flex size-6 cursor-pointer items-center justify-center rounded-md hover:bg-slate-100"
            aria-label="Close tooltip"
          >
            <Icon name="IconX" className="size-4 text-black" />
          </button>

          {/* Title */}
          <p className="pr-8 text-sm font-semibold leading-5 tracking-[-0.14px] text-black">
            The new AI edits are here!
          </p>

          {/* Description */}
          <p className="min-w-full shrink-0 whitespace-pre-wrap text-xs leading-[18px] text-slate-500">
            Enhance your clips with automatic scene changes, Zoom effects, B-Rolls and dynamic text callouts.
          </p>
        </div>
      </div>
      <style>{`
        .ai-edits-tooltip {
          opacity: 0;
          transform: translateX(20%);
          animation: fadeInSlide 300ms ease-out forwards;
        }
        
        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateX(20%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
});

AIEditsTooltip.displayName = 'AIEditsTooltip';

export default AIEditsTooltip;
